import {
  FormEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { ThemeToggle } from "./theme";
import "./styles.css";
import "./admin.css";

const ADMIN_TOKEN_STORAGE_KEY = "long-recorder-admin-token";

interface SessionSummary {
  id: string;
  recorder_name?: string;
  status: string;
  started_at: string;
  ended_at: string | null;
  mime_type: string;
  noise_floor: number;
  speech_threshold: number;
  chunk_count: number;
  stored_bytes: number;
  audio_duration_ms: number;
  likely_silent_count: number;
}

interface AdminChunk {
  id: string;
  session_id: string;
  sequence_number: number;
  started_at: string;
  ended_at: string;
  duration_ms: number;
  speech_ms: number;
  peak_rms: number;
  average_rms: number;
  likely_silent: number;
  content_type: string;
  object_key: string;
  byte_size: number;
}

interface SessionEvent {
  id: string;
  event_type: string;
  occurred_at: string;
}

interface SessionDetails {
  session: SessionSummary;
  chunks: AdminChunk[];
  events: SessionEvent[];
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "medium",
  }).format(new Date(value));
}

function formatDuration(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  return [hours, minutes, seconds]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
}

function formatBytes(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_048_576) return `${(bytes / 1_024).toFixed(1)} KB`;
  if (bytes < 1_073_741_824) {
    return `${(bytes / 1_048_576).toFixed(1)} MB`;
  }
  return `${(bytes / 1_073_741_824).toFixed(2)} GB`;
}

function displayStatus(status: string): string {
  return status.replaceAll("_", " ");
}

async function responseError(response: Response): Promise<string> {
  const body = (await response
    .json()
    .catch(() => undefined)) as { message?: string } | undefined;
  return body?.message || `Request failed with HTTP ${response.status}.`;
}

export default function RecordingsAdmin() {
  const [tokenInput, setTokenInput] = useState(
    sessionStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) ?? "",
  );
  const tokenRef = useRef(tokenInput);
  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [details, setDetails] = useState<Record<string, SessionDetails>>({});
  const [expandedSessionId, setExpandedSessionId] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [loadingSessionId, setLoadingSessionId] = useState<string>();
  const [loadingChunkIds, setLoadingChunkIds] = useState<Set<string>>(
    new Set(),
  );
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const audioUrlsRef = useRef(new Map<string, string>());
  const [error, setError] = useState<string>();
  const [authorized, setAuthorized] = useState(false);

  const authenticatedFetch = (path: string) =>
    fetch(path, {
      headers: {
        Authorization: `Bearer ${tokenRef.current}`,
      },
    });

  const loadSessions = async () => {
    if (!tokenRef.current) return;
    setLoading(true);
    setError(undefined);
    try {
      const response = await authenticatedFetch("/api/sessions");
      if (!response.ok) {
        if (response.status === 401) setAuthorized(false);
        throw new Error(await responseError(response));
      }
      const body = (await response.json()) as {
        sessions: SessionSummary[];
      };
      setSessions(body.sessions);
      setAuthorized(true);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to load recordings.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tokenRef.current) void loadSessions();
    return () => {
      for (const url of audioUrlsRef.current.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  const saveToken = (event: FormEvent) => {
    event.preventDefault();
    const token = tokenInput.trim();
    if (!token) return;
    sessionStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, token);
    tokenRef.current = token;
    void loadSessions();
  };

  const toggleSession = async (sessionId: string) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(undefined);
      return;
    }
    setExpandedSessionId(sessionId);
    if (details[sessionId]) return;

    setLoadingSessionId(sessionId);
    setError(undefined);
    try {
      const response = await authenticatedFetch(
        `/api/sessions/${encodeURIComponent(sessionId)}`,
      );
      if (!response.ok) throw new Error(await responseError(response));
      const body = (await response.json()) as SessionDetails;
      setDetails((current) => ({ ...current, [sessionId]: body }));
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to load session details.",
      );
    } finally {
      setLoadingSessionId(undefined);
    }
  };

  const fetchChunkBlob = async (chunk: AdminChunk): Promise<string> => {
    const existing = audioUrlsRef.current.get(chunk.id);
    if (existing) return existing;

    setLoadingChunkIds((current) => new Set(current).add(chunk.id));
    try {
      const response = await authenticatedFetch(
        `/api/chunks/${encodeURIComponent(chunk.id)}/download`,
      );
      if (!response.ok) throw new Error(await responseError(response));
      const url = URL.createObjectURL(await response.blob());
      audioUrlsRef.current.set(chunk.id, url);
      setAudioUrls((current) => ({ ...current, [chunk.id]: url }));
      return url;
    } finally {
      setLoadingChunkIds((current) => {
        const next = new Set(current);
        next.delete(chunk.id);
        return next;
      });
    }
  };

  const loadAudio = async (chunk: AdminChunk) => {
    setError(undefined);
    try {
      await fetchChunkBlob(chunk);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to load audio.",
      );
    }
  };

  const downloadChunk = async (chunk: AdminChunk) => {
    setError(undefined);
    try {
      const url = await fetchChunkBlob(chunk);
      const link = document.createElement("a");
      link.href = url;
      link.download = chunk.object_key.split("/").pop() || `${chunk.id}.audio`;
      link.click();
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to download audio.",
      );
    }
  };

  return (
    <main className="shell admin-shell">
      <header className="masthead admin-masthead">
        <div>
          <p className="eyebrow">Private archive</p>
          <h1>Recording Archive</h1>
          <p className="intro">
            Listen to and download your saved recordings.
          </p>
        </div>
        <div className="header-actions">
          <ThemeToggle />
          <a className="nav-link" href="/">
            Recorder
          </a>
        </div>
      </header>

      <section className="token-panel" aria-labelledby="admin-token-title">
        <div>
          <h2 id="admin-token-title">Archive access</h2>
          <p>Your access code is kept only in this browser tab.</p>
        </div>
        <form onSubmit={saveToken}>
          <label htmlFor="admin-token">Archive access code</label>
          <div className="token-row">
            <input
              id="admin-token"
              type="password"
              value={tokenInput}
              onChange={(event) => setTokenInput(event.target.value)}
              autoComplete="off"
              placeholder="Enter your access code"
            />
            <button className="secondary" disabled={loading} type="submit">
              {loading ? "Loading…" : authorized ? "Reload" : "Open archive"}
            </button>
          </div>
        </form>
      </section>

      {error && (
        <section className="admin-alert" role="alert">
          <strong>Could not load the archive</strong>
          <span>{error}</span>
        </section>
      )}

      {authorized && (
        <section className="archive" aria-live="polite">
          <div className="archive-heading">
            <div>
              <p className="eyebrow">Sessions</p>
              <h2>{sessions.length} recordings</h2>
            </div>
            <button
              className="secondary"
              disabled={loading}
              onClick={() => void loadSessions()}
            >
              Refresh
            </button>
          </div>

          {sessions.length === 0 ? (
            <div className="empty-archive">
              <strong>No recordings yet</strong>
              <p>
                Completed recorder segments will appear here after their upload
                queue is confirmed.
              </p>
            </div>
          ) : (
            <div className="session-list">
              {sessions.map((session) => {
                const isExpanded = expandedSessionId === session.id;
                const sessionDetails = details[session.id];
                return (
                  <article className="session-card" key={session.id}>
                    <button
                      className="session-summary"
                      aria-expanded={isExpanded}
                      onClick={() => void toggleSession(session.id)}
                    >
                      <span className={`status-badge ${session.status}`}>
                        {displayStatus(session.status)}
                      </span>
                      <span className="session-primary">
                        <strong>{formatDate(session.started_at)}</strong>
                        <small>
                          Recorded by {session.recorder_name || "Legacy"}
                        </small>
                      </span>
                      <span>
                        <small>Segments</small>
                        <strong>{session.chunk_count}</strong>
                      </span>
                      <span>
                        <small>Audio</small>
                        <strong>
                          {formatDuration(session.audio_duration_ms)}
                        </strong>
                      </span>
                      <span>
                        <small>Stored</small>
                        <strong>{formatBytes(session.stored_bytes)}</strong>
                      </span>
                      <span className="expand-mark" aria-hidden="true">
                        {isExpanded ? "−" : "+"}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="session-detail">
                        {loadingSessionId === session.id ? (
                          <p className="detail-loading">Loading segments…</p>
                        ) : sessionDetails ? (
                          <>
                            <div className="session-facts">
                              <span>
                                <small>Started</small>
                                {formatDate(session.started_at)}
                              </span>
                              <span>
                                <small>Ended</small>
                                {formatDate(session.ended_at)}
                              </span>
                              <span>
                                <small>Likely silent</small>
                                {session.likely_silent_count}
                              </span>
                              <span>
                                <small>Format</small>
                                {session.mime_type || "Browser default"}
                              </span>
                            </div>

                            {sessionDetails.chunks.length === 0 ? (
                              <p className="detail-loading">
                                This session has no uploaded segments.
                              </p>
                            ) : (
                              <div className="chunk-list">
                                {sessionDetails.chunks.map((chunk) => (
                                  <div className="chunk-card" key={chunk.id}>
                                    <div className="chunk-number">
                                      <small>Segment</small>
                                      <strong>
                                        {String(chunk.sequence_number).padStart(
                                          3,
                                          "0",
                                        )}
                                      </strong>
                                    </div>
                                    <div className="chunk-meta">
                                      <strong>
                                        {formatDuration(chunk.duration_ms)}
                                        {Boolean(chunk.likely_silent) && (
                                          <span className="silent-chip">
                                            likely silent
                                          </span>
                                        )}
                                      </strong>
                                      <span>{formatDate(chunk.started_at)}</span>
                                      <code>{chunk.object_key}</code>
                                      <small>
                                        {formatBytes(chunk.byte_size)} · speech{" "}
                                        {formatDuration(chunk.speech_ms)} · peak{" "}
                                        {chunk.peak_rms.toFixed(4)}
                                      </small>
                                    </div>
                                    <div className="chunk-actions">
                                      {audioUrls[chunk.id] ? (
                                        <audio
                                          controls
                                          preload="metadata"
                                          src={audioUrls[chunk.id]}
                                        />
                                      ) : (
                                        <button
                                          className="secondary"
                                          disabled={loadingChunkIds.has(
                                            chunk.id,
                                          )}
                                          onClick={() => void loadAudio(chunk)}
                                        >
                                          {loadingChunkIds.has(chunk.id)
                                            ? "Loading…"
                                            : "Listen"}
                                        </button>
                                      )}
                                      <button
                                        className="text-button"
                                        disabled={loadingChunkIds.has(chunk.id)}
                                        onClick={() =>
                                          void downloadChunk(chunk)
                                        }
                                      >
                                        Download
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        ) : null}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      )}
    </main>
  );
}
